// lib/public-tools/pipeline-seo-drugs.ts
//
// Curated drug inventory for the programmatic SEO pages at /pipeline/[drug]
// (Phase 1 of the Trial Pipeline SEO strategy, locked 2026-07-12).
//
// This list is the single source of truth for:
//   1. app/(marketing)/pipeline/[drug]/page.tsx  (generateStaticParams — only
//      these slugs render; dynamicParams=false, everything else 404s)
//   2. app/(marketing)/pipeline/page.tsx          (the hub page, grouped by class)
//   3. app/sitemap.ts                             (URL enumeration)
//   4. Same-class cross-links on each drug page   (getClassSiblings)
//
// Curation rules (deliberate — do NOT auto-generate from the RxNorm dictionary):
//   - Hand-picked drugs with active, searched-for phase 2-4 pipelines. A new
//     domain can't afford thin pages; ~50 strong pages beat 30k weak ones under
//     Google's scaled-content policy.
//   - Every entry must return non-empty CTGov lanes (the page renders a
//     soft-fail note otherwise, but persistent zero-trial entries should be
//     removed from this list).
//   - Expansion is gated on Search Console indexation ratio (>60% indexed =
//     safe to grow the list; <40% = stop and investigate).

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export type PipelineSeoGroup =
  | "metabolic"
  | "cardiovascular"
  | "oncology"
  | "immunology"
  | "neuroscience"
  | "womens-health"
  | "respiratory"

export type PipelineSeoClass = {
  key: string
  /** Human label used on hub sections + cross-link headings. */
  label: string
  group: PipelineSeoGroup
}

export type PipelineSeoDrug = {
  /** URL slug — kebab-case generic name; stable forever once published. */
  slug: string
  /** Generic / INN name — display + default CTGov query term. */
  generic: string
  /** Primary brand name(s) for display; null for investigational assets. */
  brand: string | null
  classKey: string
  /** Override CTGov query term when the generic doesn't search well (combos). */
  ctgovTerm?: string
}

// ---------------------------------------------------------------------------
// Classes
// ---------------------------------------------------------------------------

export const PIPELINE_SEO_CLASSES: PipelineSeoClass[] = [
  { key: "incretin", label: "GLP-1 & incretin therapies", group: "metabolic" },
  { key: "cardiometabolic-renal", label: "Cardiometabolic & renal", group: "metabolic" },
  { key: "mash", label: "MASH / liver disease", group: "metabolic" },
  { key: "lipid", label: "Lipid & Lp(a) therapies", group: "cardiovascular" },
  { key: "cardiomyopathy-attr", label: "Cardiomyopathy & ATTR amyloidosis", group: "cardiovascular" },
  { key: "onc-io", label: "Immuno-oncology checkpoint inhibitors", group: "oncology" },
  { key: "onc-adc", label: "Antibody-drug conjugates", group: "oncology" },
  { key: "onc-kras", label: "KRAS inhibitors", group: "oncology" },
  { key: "onc-targeted", label: "Targeted & bispecific oncology", group: "oncology" },
  { key: "immunology", label: "Immunology & inflammation", group: "immunology" },
  { key: "neuro", label: "Neuroscience", group: "neuroscience" },
  { key: "womens-health", label: "Women's health", group: "womens-health" },
  { key: "respiratory", label: "Respiratory", group: "respiratory" },
]

// ---------------------------------------------------------------------------
// Drugs (52)
// ---------------------------------------------------------------------------

export const PIPELINE_SEO_DRUGS: PipelineSeoDrug[] = [
  // --- GLP-1 & incretin (9)
  { slug: "semaglutide", generic: "semaglutide", brand: "Ozempic / Wegovy", classKey: "incretin" },
  { slug: "tirzepatide", generic: "tirzepatide", brand: "Mounjaro / Zepbound", classKey: "incretin" },
  { slug: "orforglipron", generic: "orforglipron", brand: null, classKey: "incretin" },
  { slug: "retatrutide", generic: "retatrutide", brand: null, classKey: "incretin" },
  { slug: "survodutide", generic: "survodutide", brand: null, classKey: "incretin" },
  { slug: "mazdutide", generic: "mazdutide", brand: null, classKey: "incretin" },
  { slug: "cagrilintide", generic: "cagrilintide", brand: null, classKey: "incretin" },
  { slug: "dulaglutide", generic: "dulaglutide", brand: "Trulicity", classKey: "incretin" },
  { slug: "liraglutide", generic: "liraglutide", brand: "Victoza / Saxenda", classKey: "incretin" },

  // --- Cardiometabolic & renal (3)
  { slug: "empagliflozin", generic: "empagliflozin", brand: "Jardiance", classKey: "cardiometabolic-renal" },
  { slug: "dapagliflozin", generic: "dapagliflozin", brand: "Farxiga", classKey: "cardiometabolic-renal" },
  { slug: "finerenone", generic: "finerenone", brand: "Kerendia", classKey: "cardiometabolic-renal" },

  // --- MASH / liver (2)
  { slug: "resmetirom", generic: "resmetirom", brand: "Rezdiffra", classKey: "mash" },
  { slug: "efruxifermin", generic: "efruxifermin", brand: null, classKey: "mash" },

  // --- Lipid & Lp(a) (4)
  { slug: "obicetrapib", generic: "obicetrapib", brand: null, classKey: "lipid" },
  { slug: "inclisiran", generic: "inclisiran", brand: "Leqvio", classKey: "lipid" },
  { slug: "pelacarsen", generic: "pelacarsen", brand: null, classKey: "lipid" },
  { slug: "olezarsen", generic: "olezarsen", brand: "Tryngolza", classKey: "lipid" },

  // --- Cardiomyopathy & ATTR (5)
  { slug: "mavacamten", generic: "mavacamten", brand: "Camzyos", classKey: "cardiomyopathy-attr" },
  { slug: "aficamten", generic: "aficamten", brand: null, classKey: "cardiomyopathy-attr" },
  { slug: "acoramidis", generic: "acoramidis", brand: "Attruby", classKey: "cardiomyopathy-attr" },
  { slug: "tafamidis", generic: "tafamidis", brand: "Vyndaqel", classKey: "cardiomyopathy-attr" },
  { slug: "vutrisiran", generic: "vutrisiran", brand: "Amvuttra", classKey: "cardiomyopathy-attr" },

  // --- Immuno-oncology (3)
  { slug: "pembrolizumab", generic: "pembrolizumab", brand: "Keytruda", classKey: "onc-io" },
  { slug: "nivolumab", generic: "nivolumab", brand: "Opdivo", classKey: "onc-io" },
  { slug: "durvalumab", generic: "durvalumab", brand: "Imfinzi", classKey: "onc-io" },

  // --- ADCs (3)
  { slug: "trastuzumab-deruxtecan", generic: "trastuzumab deruxtecan", brand: "Enhertu", classKey: "onc-adc" },
  { slug: "datopotamab-deruxtecan", generic: "datopotamab deruxtecan", brand: "Datroway", classKey: "onc-adc" },
  { slug: "sacituzumab-govitecan", generic: "sacituzumab govitecan", brand: "Trodelvy", classKey: "onc-adc" },

  // --- KRAS (3)
  { slug: "sotorasib", generic: "sotorasib", brand: "Lumakras", classKey: "onc-kras" },
  { slug: "adagrasib", generic: "adagrasib", brand: "Krazati", classKey: "onc-kras" },
  { slug: "divarasib", generic: "divarasib", brand: null, classKey: "onc-kras" },

  // --- Targeted & bispecific oncology (5)
  { slug: "amivantamab", generic: "amivantamab", brand: "Rybrevant", classKey: "onc-targeted" },
  { slug: "tarlatamab", generic: "tarlatamab", brand: "Imdelltra", classKey: "onc-targeted" },
  { slug: "ribociclib", generic: "ribociclib", brand: "Kisqali", classKey: "onc-targeted" },
  { slug: "zanubrutinib", generic: "zanubrutinib", brand: "Brukinsa", classKey: "onc-targeted" },
  { slug: "teclistamab", generic: "teclistamab", brand: "Tecvayli", classKey: "onc-targeted" },

  // --- Immunology & inflammation (7)
  { slug: "dupilumab", generic: "dupilumab", brand: "Dupixent", classKey: "immunology" },
  { slug: "lebrikizumab", generic: "lebrikizumab", brand: "Ebglyss", classKey: "immunology" },
  { slug: "bimekizumab", generic: "bimekizumab", brand: "Bimzelx", classKey: "immunology" },
  { slug: "risankizumab", generic: "risankizumab", brand: "Skyrizi", classKey: "immunology" },
  { slug: "guselkumab", generic: "guselkumab", brand: "Tremfya", classKey: "immunology" },
  { slug: "upadacitinib", generic: "upadacitinib", brand: "Rinvoq", classKey: "immunology" },
  { slug: "deucravacitinib", generic: "deucravacitinib", brand: "Sotyktu", classKey: "immunology" },

  // --- Neuroscience (4)
  { slug: "lecanemab", generic: "lecanemab", brand: "Leqembi", classKey: "neuro" },
  { slug: "donanemab", generic: "donanemab", brand: "Kisunla", classKey: "neuro" },
  { slug: "xanomeline-trospium", generic: "xanomeline-trospium", brand: "Cobenfy", classKey: "neuro", ctgovTerm: "xanomeline" },
  { slug: "atogepant", generic: "atogepant", brand: "Qulipta", classKey: "neuro" },

  // --- Women's health (2)
  { slug: "fezolinetant", generic: "fezolinetant", brand: "Veozah", classKey: "womens-health" },
  { slug: "elinzanetant", generic: "elinzanetant", brand: null, classKey: "womens-health" },

  // --- Respiratory (2)
  { slug: "tezepelumab", generic: "tezepelumab", brand: "Tezspire", classKey: "respiratory" },
  { slug: "nerandomilast", generic: "nerandomilast", brand: null, classKey: "respiratory" },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const bySlug = new Map(PIPELINE_SEO_DRUGS.map((d) => [d.slug, d]))
const classByKey = new Map(PIPELINE_SEO_CLASSES.map((c) => [c.key, c]))

export function getSeoDrugBySlug(slug: string): PipelineSeoDrug | null {
  return bySlug.get(slug) ?? null
}

export function getSeoClass(classKey: string): PipelineSeoClass | null {
  return classByKey.get(classKey) ?? null
}

/** CTGov query term for a drug (explicit override or the generic name). */
export function ctgovTermFor(drug: PipelineSeoDrug): string {
  return drug.ctgovTerm ?? drug.generic
}

/** Display name: "Brand (generic)" or just the generic for investigational assets. */
export function displayNameFor(drug: PipelineSeoDrug): string {
  return drug.brand ? `${drug.generic} (${drug.brand})` : drug.generic
}

/**
 * Cross-link siblings for a drug page: same class first (the strongest
 * relevance signal), then same broad group to fill, capped at `limit`.
 * Deterministic order (inventory order) so pages don't churn between builds.
 */
export function getClassSiblings(slug: string, limit = 8): PipelineSeoDrug[] {
  const drug = bySlug.get(slug)
  if (!drug) return []
  const cls = classByKey.get(drug.classKey)
  const sameClass = PIPELINE_SEO_DRUGS.filter(
    (d) => d.classKey === drug.classKey && d.slug !== slug,
  )
  const sameGroup = cls
    ? PIPELINE_SEO_DRUGS.filter(
        (d) =>
          d.classKey !== drug.classKey &&
          classByKey.get(d.classKey)?.group === cls.group,
      )
    : []
  return [...sameClass, ...sameGroup].slice(0, limit)
}

/** Hub page grouping: classes in declared order, each with its drugs. */
export function listDrugsByClass(): Array<{
  cls: PipelineSeoClass
  drugs: PipelineSeoDrug[]
}> {
  return PIPELINE_SEO_CLASSES.map((cls) => ({
    cls,
    drugs: PIPELINE_SEO_DRUGS.filter((d) => d.classKey === cls.key),
  })).filter((g) => g.drugs.length > 0)
}

// ---------------------------------------------------------------------------
// Test hooks
// ---------------------------------------------------------------------------

export const _internals = {
  bySlug,
  classByKey,
}
